import { create } from 'zustand';
import { getMeSupportTickets } from '../api/generated/me-support-tickets/me-support-tickets';
import { useToastStore } from './useToastStore';
import { showApiError } from '../errors';
import i18n from '@/i18n/config';
import type {
  CreateSupportTicketRequest,
  SupportTicketDto,
  SupportTicketStatus,
  SupportTicketType,
} from '../api/types/admin';

const {
  getApiV1MeSupportTickets,
  getApiV1MeSupportTicketsId,
  postApiV1MeSupportTickets,
  postApiV1MeSupportTicketsIdReplies,
} = getMeSupportTickets();

const t = (key: string) => i18n.t(key);

export interface MeSupportFilters {
  status?: SupportTicketStatus;
  type?: SupportTicketType;
}

interface MeSupportState {
  tickets: SupportTicketDto[];
  filters: MeSupportFilters;
  loading: boolean;
  saving: boolean;
  error: string | null;

  setFilters: (f: Partial<MeSupportFilters>) => void;
  fetchAll: () => Promise<void>;
  getById: (id: string) => Promise<SupportTicketDto | null>;
  create: (req: CreateSupportTicketRequest) => Promise<string | null>;
  reply: (id: string, body: string) => Promise<boolean>;
}

export const useMeSupportStore = create<MeSupportState>((set, get) => ({
  tickets: [],
  filters: {},
  loading: false,
  saving: false,
  error: null,

  setFilters: (f) => {
    set((s) => ({ filters: { ...s.filters, ...f } }));
  },

  fetchAll: async () => {
    set({ loading: true, error: null });
    try {
      const { status, type } = get().filters;
      const response = await getApiV1MeSupportTickets({ status, type });
      const sorted = [...(response ?? [])].sort((a, b) => {
        const da = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const db = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return db - da;
      });
      set({ tickets: sorted, loading: false });
    } catch (error) {
      const apiError = showApiError(error);
      set({ error: apiError.code, loading: false });
    }
  },

  getById: async (id) => {
    try {
      return await getApiV1MeSupportTicketsId(id);
    } catch (error) {
      showApiError(error);
      return null;
    }
  },

  create: async (req) => {
    set({ saving: true });
    try {
      const created = await postApiV1MeSupportTickets(req);
      set({ saving: false });
      useToastStore.getState().addToast(t('support:messages.created'), 'success');
      return created?.id ?? null;
    } catch (error) {
      showApiError(error);
      set({ saving: false });
      return null;
    }
  },

  reply: async (id, body) => {
    set({ saving: true });
    try {
      const updated = await postApiV1MeSupportTicketsIdReplies(id, { body });
      set((s) => ({
        tickets: s.tickets.map((t0) => (t0.id === id ? { ...t0, ...updated } : t0)),
        saving: false,
      }));
      useToastStore.getState().addToast(t('support:messages.replySent'), 'success');
      return true;
    } catch (error) {
      showApiError(error);
      set({ saving: false });
      return false;
    }
  },
}));
